import React from 'react';
import { format } from 'date-fns';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CheckCircle2, Clock, Loader2 } from 'lucide-react';
import type { AuditTrailRow } from '@/utils/AuditTrail/types';
import { useAuditTrailHistory } from '@/hooks/AuditTrail/useAuditTrailHistory';
import { StatusBadge } from './StatusBadge';
import { EventTimeline } from './EventTimeline';
import { WorkflowStages } from './WorkflowStages';

interface DetailDrawerProps {
  row: AuditTrailRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DetailDrawer: React.FC<DetailDrawerProps> = ({ row, open, onOpenChange }) => {
  const { history, historyError } = useAuditTrailHistory(row, open);

  if (!row) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[600px] sm:max-w-none flex flex-col p-0 gap-0"
      >
        {/* Drawer header */}
        <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              <SheetTitle className="text-base font-semibold leading-tight">
                {row.employeeName}
              </SheetTitle>
              <p className="text-sm text-muted-foreground">
                Week ending {format(row.weekEnding, 'dd/MM/yyyy')}
              </p>
              <div className="pt-0.5">
                <StatusBadge status={row.status} />
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Drawer body */}
        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="h-full">
            <div className="px-6 py-5 space-y-6">
              {/* Section 1: Event Timeline */}
              <section>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  Event Timeline
                </h3>
                {historyError ? (
                  <p className="text-sm text-muted-foreground">{historyError}</p>
                ) : !history ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading history…
                  </div>
                ) : (
                  <EventTimeline
                    events={history.events}
                    isAutoApproved={history.isAutoApproved}
                    autoApprovedDate={history.isAutoApproved ? row.lastEventDate : undefined}
                  />
                )}
              </section>

              {/* Section 2: Workflow Stages */}
              {history && !history.isAutoApproved && history.stages.length > 0 && (
                <>
                  <Separator />
                  <section>
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                      Workflow Stages
                    </h3>
                    <WorkflowStages stages={history.stages} />
                  </section>
                </>
              )}
            </div>
          </ScrollAreaViewport>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
